import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, Subject, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { buildFinancialApiUrl } from '../utils/financial-api-url.util';
import { ErrorService } from '../shared/services/error.service';

export interface CashflowPagedResult {
  items: any[];
  page: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
}

export interface PaginationOptions {
  page?: number;
  pageSize?: number;
}

@Injectable({ providedIn: 'root' })
export class CashflowService {
  private refreshRequested = new Subject<void>();

  private financialUrl(path = ''): string {
    return buildFinancialApiUrl('cashflow', path);
  }

  constructor(
    private http: HttpClient,
    private errorService: ErrorService
  ) {}

  /** Emitido quando outra tela (ex.: recebimento) altera dados do caixa; o componente pode refazer a busca. */
  get onRefreshRequested(): Observable<void> {
    return this.refreshRequested.asObservable();
  }

  /** Chamar após marcar conta a receber como recebida para que a tela de fluxo de caixa atualize. */
  requestRefresh(): void {
    this.refreshRequested.next();
  }

  getByDateRange(start: string, end: string, options?: PaginationOptions): Observable<CashflowPagedResult> {
    let params = new HttpParams().set('start', start).set('end', end);
    if (options?.page) params = params.set('page', options.page.toString());
    if (options?.pageSize) params = params.set('pageSize', options.pageSize.toString());
    
    return this.http.get<any>(this.financialUrl('/date-range'), { params }).pipe(
      map(resp => this.mapPagedResponse(resp)),
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao buscar fluxo de caixa por período');
        return throwError(() => err);
      })
    );
  }

  /**
   * Lista lançamentos por centro de custo (com paginação).
   * costCenterId opcional: se omitido, retorna todos os lançamentos.
   */
  getByCostCenter(costCenterId?: number | null, options?: PaginationOptions): Observable<CashflowPagedResult> {
    let params = new HttpParams();
    if (costCenterId != null && costCenterId !== undefined) {
      params = params.set('costCenterId', String(costCenterId));
    }
    if (options?.page) params = params.set('page', options.page.toString());
    if (options?.pageSize) params = params.set('pageSize', options.pageSize.toString());

    return this.http.get<any>(this.financialUrl('/costcenter'), { params }).pipe(
      map(resp => this.mapPagedResponse(resp)),
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao buscar fluxo de caixa por centro de custo');
        return throwError(() => err);
      })
    );
  }

  getTotalByType(type: string, start?: string, end?: string): Observable<{ total: number }> {
    let params = new HttpParams();
    if (start) params = params.set('start', start);
    if (end) params = params.set('end', end);
    return this.http.get<{ total: number }>(this.financialUrl(`/total/${encodeURIComponent(type)}`), { params }).pipe(
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao buscar total por tipo');
        return throwError(() => err);
      })
    );
  }

  getAll(): Observable<any[]> {
    return this.http.get<any[]>(`${this.financialUrl()}`).pipe(
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao buscar fluxo de caixa');
        return throwError(() => err);
      })
    );
  }

  getById(id: number): Observable<any> {
    return this.http.get<any>(this.financialUrl(`/${id}`)).pipe(
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao buscar fluxo de caixa');
        return throwError(() => err);
      })
    );
  }

  create(payload: any): Observable<any> {
    return this.http.post<any>(`${this.financialUrl()}`, payload).pipe(
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao criar fluxo de caixa');
        return throwError(() => err);
      })
    );
  }

  update(id: number, payload: any): Observable<any> {
    return this.http.put<any>(this.financialUrl(`/${id}`), payload).pipe(
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao atualizar fluxo de caixa');
        return throwError(() => err);
      })
    );
  }

  delete(id: number): Observable<void> {
    return this.http.delete<void>(this.financialUrl(`/${id}`)).pipe(
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao deletar fluxo de caixa');
        return throwError(() => err);
      })
    );
  }

  private mapPagedResponse(response: any): CashflowPagedResult {
    if (!response || typeof response !== 'object') {
      return { items: [], page: 1, pageSize: 20, totalItems: 0, totalPages: 0 };
    }
    const rawItems = Array.isArray(response?.Items)
      ? response.Items
      : Array.isArray(response?.items)
        ? response.items
        : Array.isArray(response?.data)
          ? response.data
          : Array.isArray(response?.value)
            ? response.value
            : Array.isArray(response?.Result)
              ? response.Result
              : Array.isArray(response)
                ? response
                : [];

    const items = rawItems;

    const page = response?.Page ?? response?.page ?? 1;
    const pageSize = response?.PageSize ?? response?.pageSize ?? items.length;
    const totalItems = response?.TotalItems ?? response?.totalItems ?? response?.TotalRecords ?? response?.totalRecords ?? response?.totalCount ?? response?.TotalCount ?? items.length;
    const totalPages = response?.TotalPages ?? response?.totalPages ?? (pageSize > 0 ? Math.ceil(totalItems / pageSize) : 1);

    return {
      items,
      page,
      pageSize,
      totalItems,
      totalPages,
    };
  }
}
